import { useEffect, useState, useCallback } from "react";
import { opApi, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Info, Send, Trash2 } from "lucide-react";
import { MorrLink } from "@/components/mc/MorrLink";
import { LEVELS } from "@/components/mc/MorrMenu";

const sel = "border border-input bg-background h-9 px-2 text-sm";
const ROLE_HELP = { owner: "Everything, including team and cart type.", manager: "Day-to-day: calendar, services, clients, reports, marketing. Can't change team or cart type.", staff: "Their own calendar and clients. Sees the shop calendar only if you turn it on." };

const BADGE = {
  invited: ["Invited", "bg-amber-100 text-amber-800 border-amber-300"],
  active: ["Active", "bg-emerald-100 text-emerald-800 border-emerald-300"],
  removed: ["Removed", "bg-red-100 text-red-800 border-red-300"],
};

// Morr's latest word wins (webhook or MorrAccess list); otherwise infer from sign-ins.
export function teamStatus(m) {
  const s = (m.access_status || "").toLowerCase();
  if (["removed", "revoked", "suspended", "disabled", "inactive"].includes(s)) return "removed";
  if (s === "active" || m.has_signed_in) return "active";
  return "invited";
}

function MemberRow({ m, providers, canEdit, reload }) {
  const patch = (b) => opApi.patch(`/tenant/team/${m.id}`, b).then(() => { toast.success("Updated"); reload(); }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  const st = teamStatus(m);
  return (
    <div className="flex flex-wrap items-center gap-2 p-3 text-sm" data-testid={`team-member-${m.id}`}>
      <div className="flex-1 min-w-[180px]">
        <div className="font-medium flex items-center gap-2">{m.name || m.email}
          <span className={`text-[10px] uppercase tracking-wider border px-1.5 py-0.5 ${BADGE[st][1]}`} data-testid={`team-status-${m.id}`}>{BADGE[st][0]}</span>
          {LEVELS[m.permission_level] && <span className="text-[10px] uppercase tracking-wider border border-border px-1.5 py-0.5 text-muted-foreground" title="Set in Morr Access" data-testid={`team-level-${m.id}`}>{LEVELS[m.permission_level]}</span>}
        </div>
        <div className="text-xs text-muted-foreground">{m.email}{m.phone ? ` · ${m.phone}` : ""} · {st === "active" ? "Has access" : st === "removed" ? "No longer has access in MorrAccess" : "Invited — not signed in yet"}</div>
      </div>
      <select className={sel} disabled={!canEdit} value={m.role} onChange={(e) => patch({ role: e.target.value })} data-testid={`team-role-${m.id}`}>
        <option value="owner">Owner</option><option value="manager">Manager</option><option value="staff">Staff</option>
      </select>
      {m.role === "staff" && (
        <select className={sel} disabled={!canEdit} value={m.provider_id || ""} onChange={(e) => patch({ provider_id: e.target.value })} data-testid={`team-provider-${m.id}`}>
          <option value="">Pick calendar…</option>{providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      )}
      {canEdit && <>
        <Switch checked={m.active !== false} onCheckedChange={(v) => patch({ active: v })} data-testid={`team-active-${m.id}`} />
        <Button size="sm" variant="ghost" className="rounded-none" onClick={() => opApi.post(`/tenant/team/${m.id}/resend`).then(() => toast.success("Invite sent")).catch((e) => toast.error(apiError(e.response?.data?.detail)))} data-testid={`team-resend-${m.id}`}><Send className="h-3.5 w-3.5" /></Button>
        <Button size="sm" variant="ghost" className="rounded-none text-destructive" onClick={() => opApi.delete(`/tenant/team/${m.id}`).then(reload).catch((e) => toast.error(apiError(e.response?.data?.detail)))} data-testid={`team-remove-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
      </>}
    </div>
  );
}

export function TeamSettings() {
  const [data, setData] = useState(null);
  const [providers, setProviders] = useState([]);
  const [f, setF] = useState({ email: "", name: "", role: "staff", provider_id: "" });
  const reload = useCallback(() => opApi.get("/tenant/team").then(({ data: d }) => setData(d)).catch(() => {}), []);
  useEffect(() => { reload(); opApi.get("/tenant/providers").then(({ data: p }) => setProviders(p || [])).catch(() => {}); }, [reload]);
  if (!data) return null;
  const canEdit = data.your_role === "owner";
  const add = () => opApi.post("/tenant/team", { ...f, provider_id: f.role === "staff" ? f.provider_id : null })
    .then(() => { toast.success(data.invites_via_access ? `Invite sent to ${f.email}` : `Added ${f.email}. No invite sent: this shop isn't linked to Morr yet.`); setF({ email: "", name: "", role: "staff", provider_id: "" }); reload(); })
    .catch((e) => toast.error(apiError(e.response?.data?.detail)));

  return (
    <div className="space-y-4" data-testid="team-settings">
      <div className="flex gap-2 text-xs bg-secondary p-3" data-testid="team-morraccess-note">
        <Info className="h-4 w-4 shrink-0 mt-0.5" />
        <span>{data.invites_via_access
          ? `Each person signs in with their own MorrAccess account. Adding someone here sends them a MorrAccess invite and uses one ${data.app === "morrconnect" ? "MorrConnect" : "MorrCart"} seat. Removing them takes away their access.`
          : "This shop isn't linked to a Morr business yet, so MorrAccess can't send invites. You can still add people here; ask your Morr Access admin to link the shop so they get their invite."}</span>
      </div>
      <div className="border border-primary/40 p-3 flex flex-wrap items-center gap-3" data-testid="team-morr-manage">
        <div className="flex-1 min-w-[220px] text-sm">
          <div className="font-semibold">Option B: Manage team in Morr Access</div>
          <div className="text-xs text-muted-foreground">Invite, remove, resend invites, per-app access and seats, all in one place.</div>
        </div>
        <MorrLink to="team" label="Manage team in Morr Access" testId="team-morr-manage-link" />
      </div>
      {data.morr_ok === false && (
        <div className="flex flex-wrap items-center gap-3 text-sm border border-amber-300 bg-amber-50 text-amber-900 p-3" data-testid="team-morr-unavailable">
          <span className="flex-1">Couldn't load team status from Morr right now, so Invited / Active / Removed isn't shown.</span>
          <Button size="sm" variant="outline" className="rounded-none" onClick={reload} data-testid="team-morr-retry">Retry</Button>
        </div>
      )}
      {data.seats && (
        <div className="flex flex-wrap gap-4 text-sm border border-border p-3" data-testid="team-seats">
          <span>Seats used: <strong data-testid="team-seats-used">{data.seats.used ?? 0}</strong> of <strong data-testid="team-seats-limit">{data.seats.limit ?? "unlimited"}</strong></span>
          <span className="text-muted-foreground">Pending invites: <span data-testid="team-seats-pending">{data.seats.pending_invites ?? 0}</span></span>
          <span className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground" data-testid="team-from-morr">From Morr</span>
        </div>
      )}
      {canEdit && (
        <div className="border border-border p-3 space-y-2" data-testid="team-add-form">
          <div className="text-sm font-semibold" data-testid="team-quick-invite-title">Option A: Quick invite <span className="text-xs font-normal text-muted-foreground">(sent through Morr Access)</span></div>
          <div className="grid sm:grid-cols-2 gap-2">
            <Input className="rounded-none" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="team-add-email" />
            <Input className="rounded-none" placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="team-add-name" />
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <select className={sel} value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="team-add-role"><option value="staff">Staff</option><option value="manager">Manager</option><option value="owner">Owner</option></select>
            {f.role === "staff" && <select className={sel} value={f.provider_id} onChange={(e) => setF({ ...f, provider_id: e.target.value })} data-testid="team-add-provider"><option value="">Their calendar…</option>{providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>}
            <Button className="rounded-none" onClick={add} disabled={!f.email} data-testid="team-add-btn">Add &amp; send invite</Button>
          </div>
          <p className="text-xs text-muted-foreground">{ROLE_HELP[f.role]}</p>
        </div>
      )}
      <div className="border border-border divide-y" data-testid="team-list">
        {data.members.length === 0 && <div className="p-4 text-sm text-muted-foreground">No team members added yet.</div>}
        {data.members.map((m) => <MemberRow key={m.id} m={m} providers={providers} canEdit={canEdit} reload={reload} />)}
      </div>
      {data.other_logins.length > 0 && (
        <div data-testid="team-other-logins">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Also signed in to this {data.app === "morrconnect" ? "business" : "shop"}</div>
          {data.other_logins.map((o) => <div key={o.email} className="text-sm py-1">{o.name || o.email} <span className="text-muted-foreground">· {o.email} · {o.role === "owner" ? "Owner" : "Manager (full day-to-day access until you add them above)"}</span></div>)}
        </div>
      )}
    </div>
  );
}
